import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { 
  Building2, 
  MapPin, 
  Calendar,
  Euro,
  ExternalLink,
  Heart,
  Loader2,
  ArrowLeft,
  FileText,
  CheckCircle2,
  Globe,
  Clock,
  Tag,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import DCEOrderModal from "../components/DCEOrderModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [tender, setTender] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showDCEModal, setShowDCEModal] = useState(false);
  
  // Get match reasons from navigation state (passed from recommendations)
  const matchReasons = location.state?.match_reasons || [];
  const matchScore = location.state?.match_score || null;

  // Helpers extracted to remove nested ternaries (code-quality fix)
  const getDeadlineBadgeClass = (days) => {
    if (days <= 7) return "bg-red-100 text-red-700";
    if (days <= 14) return "bg-yellow-100 text-yellow-700";
    return "bg-green-100 text-green-700";
  };
  const SOURCE_BADGE_CLASSES = {
    BOAMP: "bg-blue-50 text-blue-700 border-blue-200",
    DECP: "bg-green-50 text-green-700 border-green-200",
    TED: "bg-yellow-50 text-yellow-700 border-yellow-200",
    SNCF: "bg-red-50 text-red-700 border-red-200",
    ADP: "bg-purple-50 text-purple-700 border-purple-200",
    RATP: "bg-teal-50 text-teal-700 border-teal-200",
    MSEC: "bg-orange-50 text-orange-700 border-orange-200",
    DEMATIS: "bg-pink-50 text-pink-700 border-pink-200",
  };
  const getSourceBadgeClass = (source) =>
    SOURCE_BADGE_CLASSES[source] || "bg-slate-50 text-slate-600";

  const fetchTender = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/tenders/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTender(response.data);
    } catch (error) {
      console.error("Failed to fetch tender:", error);
      toast.error("Marché non trouvé");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
    // API_URL/axios/toast are stable module-scope constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const checkFavorite = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.some(f => f.id === id));
    } catch (error) {
      console.error("Failed to check favorite status:", error);
    }
    // API_URL/axios are stable module-scope constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchTender();
    checkFavorite();
  }, [fetchTender, checkFavorite]);

  const toggleFavorite = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      if (isFavorite) {
        await axios.delete(`${API_URL}/api/favorites/${id}`, { headers });
        setIsFavorite(false);
        toast.success("Retiré des favoris");
      } else {
        await axios.post(`${API_URL}/api/favorites/${id}`, {}, { headers });
        setIsFavorite(true);
        toast.success("Ajouté aux favoris");
      }
    } catch (error) {
      toast.error("Erreur lors de la mise à jour des favoris");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!tender) return null;

  const deadlineDate = new Date(tender.deadline);
  const daysLeft = Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6 text-slate-600 hover:text-slate-900"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Badge className="bg-violet-100 text-violet-700">
                    {tender.sector}
                  </Badge>
                  <Badge variant="outline" className="text-slate-600">
                    {tender.sub_sector}
                  </Badge>
                  <Badge 
                    className={getDeadlineBadgeClass(daysLeft)}
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    {daysLeft > 0 ? `${daysLeft} jours restants` : "Expiré"}
                  </Badge>
                  <Badge 
                    variant="outline"
                    className={getSourceBadgeClass(tender.source)}
                  >
                    Source: {tender.source === "TED" ? "TED Europa" : tender.source}
                  </Badge>
                </div>
                <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                  {tender.title}
                </h1>
                <p className="text-slate-600">
                  Référence : {tender.reference}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleFavorite}
                  className={isFavorite ? "text-red-500 border-red-200" : ""}
                  data-testid="favorite-detail-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-6">
              {/* Description */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <FileText className="w-5 h-5 text-violet-600" />
                    Description du marché
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 leading-relaxed">
                    {tender.description}
                  </p>
                </CardContent>
              </Card>

              {/* Why this tender - AI Matching Reasons */}
              {matchReasons && matchReasons.length > 0 && (
                <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-indigo-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg text-violet-800">
                      <Sparkles className="w-5 h-5 text-violet-600" />
                      Pourquoi ce marché vous correspond
                      {matchScore && (
                        <Badge className="ml-2 bg-violet-600 text-white">
                          {matchScore}% de compatibilité
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {matchReasons.map((reason, index) => (
                        <li key={`item-${index}`} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-violet-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle2 className="w-4 h-4 text-violet-700" />
                          </div>
                          <span className="text-slate-700">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Key Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations clés</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Acheteur</p>
                      <p className="text-slate-900 font-medium">{tender.buyer}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Localisation</p>
                      <p className="text-slate-900 font-medium">{tender.department}</p>
                      <p className="text-slate-600 text-sm">{tender.region}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start gap-3">
                    <Euro className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Budget estimé</p>
                      <p className="text-slate-900 font-medium">{tender.estimated_value}</p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Date limite</p>
                      <p className="text-slate-900 font-medium">
                        {deadlineDate.toLocaleDateString('fr-FR', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Source</p>
                      <p className="text-slate-900 font-medium">{tender.source}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <Card className="bg-gradient-to-br from-violet-600 to-indigo-600 border-0 text-white">
                <CardContent className="p-6 space-y-4">
                  <h3 className="font-display font-semibold text-lg">Intéressé par ce marché ?</h3>
                  <p className="text-white/80 text-sm">
                    Consultez l'avis officiel pour accéder au dossier de consultation complet.
                  </p>
                  <Button 
                    className="w-full bg-white text-violet-700 hover:bg-slate-100"
                    onClick={() => window.open(tender.url, '_blank')}
                    data-testid="view-official-btn"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    Voir l'avis officiel
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {/* DCE Button */}
                  <Button 
                    className="w-full bg-amber-500 text-white hover:bg-amber-600 mt-3"
                    onClick={() => setShowDCEModal(true)}
                    data-testid="order-dce-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Commander le DCE sous 24h
                  </Button>
                  <p className="text-xs text-white/70 text-center mt-2">
                    À partir de 2€ - Livraison par email
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>

      {/* DCE Order Modal */}
      <DCEOrderModal 
        isOpen={showDCEModal} 
        onClose={() => setShowDCEModal(false)} 
        tender={tender}
      />
    </DashboardLayout>
  );
};

export default TenderDetailPage;
